import React, { useState, useEffect, useCallback, memo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { auth } from "@/shared/api/graphqlClient";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import LanguageSwitcher from "@/shared/components/shared/LanguageSwitcher";
import {
  LayoutDashboard, ArrowDownToLine, ArrowUpFromLine,
  Receipt, Settings, LogOut, Menu, X, Zap, Bell, User,
  Server, Route, Webhook, FileText, ChevronLeft, Building2, Users,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import NotificationDropdown from "@/shared/components/shared/NotificationDropdown";
import { Toaster } from "@/shared/components/ui/toaster";
import { motion, AnimatePresence } from "framer-motion";

/* ══════════════════════════════════════════
   GLOBAL CSS — injected once
══════════════════════════════════════════ */
const GLOBAL_CSS_EN = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=Geist+Mono:wght@400;500;600&family=Syne:wght@600;700;800&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --en-sky:     #0ea5e9;
  --en-sky4:    #38bdf8;
  --en-cyan4:   #22d3ee;
  --en-blue:    #3b82f6;
  --en-emerald: #34d399;
  --en-rose:    #fb7185;
  --en-n950:    #020810;
  --en-n900:    #040c1a;
  --en-n850:    #060e1c;
  --en-b1:      rgba(34,211,238,0.08);
  --en-b2:      rgba(34,211,238,0.18);
  --en-t1:      #e0f4ff;
  --en-t2:      #9fc4de;
  --en-t3:      #52809e;
  --en-t4:      #284460;
  --en-t5:      #162a3e;
  --en-fd:      'Outfit', sans-serif;
  --en-fs:      'Syne', sans-serif;
  --en-fm:      'Geist Mono', monospace;
  --en-out:     cubic-bezier(0.22,1,0.36,1);
  --en-spring:  cubic-bezier(0.34,1.56,0.64,1);
}

html { scroll-behavior: smooth; }
body { background: var(--en-n950); color: var(--en-t1); font-family: var(--en-fd); -webkit-font-smoothing: antialiased; }
*::-webkit-scrollbar { width: 3px; height: 3px; }
*::-webkit-scrollbar-thumb { background: rgba(34,211,238,0.16); border-radius: 99px; }
*::-webkit-scrollbar-track { background: transparent; }

/* ══ SIDEBAR ══ */
.en-sb {
  position: fixed; top: 0; left: 0; bottom: 0;
  display: flex; flex-direction: column;
  background:
    radial-gradient(ellipse 140% 35% at 50% -1%, rgba(14,165,233,0.15), transparent 55%),
    linear-gradient(180deg, #03091c 0%, #020810 55%, #01060e 100%);
  border-right: 1px solid rgba(34,211,238,0.07);
  z-index: 50; overflow: hidden;
  transition: width 0.38s cubic-bezier(0.22,1,0.36,1);
  will-change: width;
}

/* Top glow bar */
.en-sb::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(14,165,233,0.75) 30%, rgba(34,211,238,0.85) 50%, rgba(59,130,246,0.7) 70%, transparent);
  z-index: 5; pointer-events: none;
  animation: en-sbglow 5s ease-in-out infinite;
}
@keyframes en-sbglow { 0%,100%{opacity:0.35} 50%{opacity:1} }

/* Bottom fade overlay */
.en-sb::after {
  content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 90px;
  background: linear-gradient(to top, rgba(1,5,12,0.95), transparent);
  z-index: 4; pointer-events: none;
}

/* ── LOGO ── */
.en-logo {
  height: 70px;
  display: flex; align-items: center;
  padding: 0 16px; gap: 13px;
  border-bottom: 1px solid rgba(34,211,238,0.07);
  flex-shrink: 0; text-decoration: none;
  position: relative; overflow: hidden; cursor: pointer;
  transition: background 0.22s;
  z-index: 2;
}
.en-logo:hover { background: rgba(14,165,233,0.05); }

.en-orb {
  width: 40px; height: 40px; border-radius: 13px;
  background: linear-gradient(135deg, #0ea5e9, #22d3ee, #3b82f6);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; position: relative; overflow: hidden;
  box-shadow: 0 0 0 1px rgba(34,211,238,0.42), 0 4px 20px rgba(14,165,233,0.36);
  transition: box-shadow 0.3s, transform 0.4s var(--en-spring);
}
.en-orb::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 48%;
  background: rgba(255,255,255,0.22); border-radius: inherit;
}
.en-orb svg { width: 18px; height: 18px; color: #fff; position: relative; z-index: 1; }
.en-logo:hover .en-orb {
  box-shadow: 0 0 0 2px rgba(34,211,238,0.7), 0 6px 28px rgba(34,211,238,0.52);
  transform: scale(1.08) rotate(-7deg);
}

.en-logo-name {
  font-family: var(--en-fs);
  font-size: 1.15rem; font-weight: 800; color: var(--en-t1);
  letter-spacing: -0.02em; white-space: nowrap; line-height: 1;
}
.en-logo-tag {
  font-family: var(--en-fm);
  font-size: 0.5rem; font-weight: 500; color: var(--en-t4);
  text-transform: uppercase; letter-spacing: 0.24em; margin-top: 4px; white-space: nowrap;
}

/* ── NAV ── */
.en-nav {
  flex: 1; padding: 14px 10px;
  overflow-y: auto; overflow-x: hidden;
  position: relative; z-index: 2;
}
.en-nav::-webkit-scrollbar { width: 0; }

.en-sec-lbl {
  font-family: var(--en-fm);
  font-size: 0.5rem; font-weight: 600;
  color: rgba(34,211,238,0.22);
  text-transform: uppercase; letter-spacing: 0.28em;
  padding: 0 12px; margin: 6px 0 8px; white-space: nowrap;
}
.en-sep {
  height: 1px; margin: 8px 12px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.09), transparent);
}

/* ── NAV ITEM ── */
.en-item {
  display: flex; align-items: center; gap: 11px;
  padding: 0 12px; height: 46px;
  border-radius: 12px; margin-bottom: 3px;
  text-decoration: none;
  font-family: var(--en-fd); font-size: 0.9rem; font-weight: 500;
  color: var(--en-t3);
  border: 1px solid transparent;
  position: relative; overflow: hidden; cursor: pointer; white-space: nowrap;
  transition: color 0.2s, background 0.2s, border-color 0.2s, box-shadow 0.2s;
}

/* Shimmer sweep on hover */
.en-item::after {
  content: '';
  position: absolute; top: 0; left: -130%; width: 70%; bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(14,165,233,0.09), transparent);
  transform: skewX(-16deg);
  transition: left 0.5s var(--en-out); pointer-events: none;
}
.en-item:hover { color: var(--en-t1); background: rgba(14,165,233,0.08); border-color: rgba(34,211,238,0.16); }
.en-item:hover::after { left: 160%; }

/* Active state */
.en-item.on {
  color: #fff;
  background: linear-gradient(90deg, rgba(14,165,233,0.18) 0%, rgba(34,211,238,0.06) 100%);
  border-color: rgba(34,211,238,0.24);
  box-shadow: inset 0 1px 0 rgba(56,189,248,0.15), 0 2px 20px rgba(14,165,233,0.15);
}
.en-item.on::after { display: none; }

/* Active accent bar */
.en-item.on::before {
  content: '';
  position: absolute; left: 0; top: 18%; bottom: 18%;
  width: 3px; border-radius: 0 3px 3px 0;
  background: linear-gradient(180deg, #38bdf8, #22d3ee, #0ea5e9);
  box-shadow: 0 0 14px rgba(34,211,238,0.95), 0 0 6px rgba(34,211,238,0.5);
}

/* Icon box */
.en-ico {
  width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.025);
  transition: background 0.22s, box-shadow 0.22s, transform 0.22s var(--en-spring);
}
.en-ico svg { width: 15px; height: 15px; }
.en-item:hover .en-ico {
  background: rgba(14,165,233,0.16);
  box-shadow: 0 0 12px rgba(14,165,233,0.22);
  transform: scale(1.1);
}
.en-item.on .en-ico {
  background: linear-gradient(135deg, rgba(14,165,233,0.3), rgba(34,211,238,0.12));
  box-shadow: 0 2px 14px rgba(14,165,233,0.26);
}
.en-item.on .en-ico svg { filter: drop-shadow(0 0 4px rgba(34,211,238,0.7)); }

.en-item-txt { flex: 1; white-space: nowrap; letter-spacing: 0.005em; }

.en-badge {
  font-family: var(--en-fm);
  font-size: 0.5rem; font-weight: 600;
  padding: 2px 7px; border-radius: 99px;
  background: rgba(14,165,233,0.1); color: var(--en-sky4);
  border: 1px solid rgba(14,165,233,0.22);
  letter-spacing: 0.08em; text-transform: uppercase;
}

/* Tooltip (collapsed) */
.en-tipwrap { position: relative; }
.en-tip {
  position: absolute; left: calc(100% + 14px); top: 50%; transform: translateY(-50%);
  background: rgba(4,9,24,0.98); border: 1px solid var(--en-b2);
  border-radius: 10px; padding: 6px 14px;
  font-family: var(--en-fd); font-size: 0.82rem; font-weight: 600; color: var(--en-t1);
  white-space: nowrap; pointer-events: none; opacity: 0;
  transition: opacity 0.18s; z-index: 100;
  box-shadow: 0 4px 20px rgba(0,0,0,0.55), 0 0 0 1px rgba(34,211,238,0.08);
}
.en-tip::before {
  content: ''; position: absolute; left: -5px; top: 50%;
  width: 8px; height: 8px;
  background: rgba(4,9,24,0.98);
  border-left: 1px solid var(--en-b2); border-bottom: 1px solid var(--en-b2);
  transform: translateY(-50%) rotate(45deg);
}
.en-tipwrap:hover .en-tip { opacity: 1; }

/* ── ENGINE CARD ── */
.en-ecard {
  margin: 0 10px 70px;
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(14,165,233,0.09), rgba(34,211,238,0.03));
  border: 1px solid rgba(34,211,238,0.13);
  position: relative; overflow: hidden; z-index: 2;
  transition: border-color 0.28s, box-shadow 0.28s, transform 0.28s;
}
.en-ecard:hover {
  border-color: rgba(34,211,238,0.32);
  box-shadow: 0 4px 36px rgba(14,165,233,0.16);
  transform: translateY(-2px);
}
.en-ecard::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.55), transparent);
}
.en-ecard::after {
  content: ''; position: absolute; bottom: -32px; right: -32px;
  width: 120px; height: 120px; border-radius: 50%;
  background: radial-gradient(circle, rgba(14,165,233,0.1), transparent 70%);
  pointer-events: none;
}
.en-ecard-in { padding: 16px; position: relative; z-index: 1; }
.en-ecard-top { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.en-ecard-av {
  width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0;
  background: linear-gradient(135deg, rgba(14,165,233,0.3), rgba(34,211,238,0.14));
  border: 1px solid rgba(34,211,238,0.26);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 0 16px rgba(34,211,238,0.16);
  transition: box-shadow 0.25s, transform 0.25s var(--en-spring);
}
.en-ecard:hover .en-ecard-av { box-shadow: 0 0 28px rgba(34,211,238,0.34); transform: scale(1.07); }
.en-ecard-av svg { width: 18px; height: 18px; color: #22d3ee; }
.en-ecard-lbl { font-family: var(--en-fm); font-size: 0.5rem; font-weight: 600; color: rgba(34,211,238,0.46); text-transform: uppercase; letter-spacing: 0.2em; margin-bottom: 3px; }
.en-ecard-name { font-family: var(--en-fs); font-size: 0.9rem; font-weight: 700; color: var(--en-t1); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; letter-spacing: -0.01em; }
.en-ecard-div { height: 1px; background: linear-gradient(90deg, transparent, rgba(34,211,238,0.1), transparent); margin: 0 0 11px; }
.en-ecard-bot { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.en-ecard-id-lbl { font-family: var(--en-fm); font-size: 0.54rem; font-weight: 600; color: var(--en-t4); text-transform: uppercase; letter-spacing: 0.12em; }
.en-ecard-id-val { font-family: var(--en-fm); font-size: 0.65rem; color: rgba(34,211,238,0.65); background: rgba(34,211,238,0.06); border: 1px solid rgba(34,211,238,0.13); border-radius: 6px; padding: 2px 8px; }
.en-ecard-status { display: flex; align-items: center; gap: 5px; font-family: var(--en-fm); font-size: 0.62rem; font-weight: 600; color: var(--en-emerald); }
.en-ecard-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--en-emerald); box-shadow: 0 0 10px rgba(52,211,153,0.85); animation: en-ecdot 2.4s ease-in-out infinite; }
@keyframes en-ecdot { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.6);opacity:0.45} }

/* ── COLLAPSE BUTTON ── */
.en-togwrap {
  position: absolute; bottom: 20px; left: 0; right: 0;
  display: flex; justify-content: center; z-index: 6; pointer-events: none;
}
.en-tog {
  pointer-events: all;
  display: flex; align-items: center; gap: 8px;
  padding: 8px 18px; border-radius: 99px;
  background: rgba(14,165,233,0.07);
  border: 1px solid rgba(34,211,238,0.17);
  color: var(--en-t3); cursor: pointer;
  position: relative; overflow: hidden;
  transition: color 0.22s, border-color 0.25s, box-shadow 0.25s, transform 0.22s, background 0.22s;
  white-space: nowrap; font-family: var(--en-fd);
}
.en-tog::before { content: ''; position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(34,211,238,0.07), transparent); opacity: 0; transition: opacity 0.25s; }
.en-tog:hover {
  color: #22d3ee; border-color: rgba(34,211,238,0.55);
  box-shadow: 0 0 30px rgba(34,211,238,0.24), inset 0 0 16px rgba(34,211,238,0.04);
  transform: translateY(-2px) scale(1.04);
}
.en-tog:hover::before { opacity: 1; }
.en-tog svg { width: 13px; height: 13px; flex-shrink: 0; }
.en-tog-txt { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; }
.en-tog.pill { padding: 10px; border-radius: 50%; gap: 0; }
.en-tog.pill .en-tog-txt { display: none; }

/* ══ HEADER ══ */
.en-header {
  height: 70px;
  background: rgba(2,7,14,0.85); backdrop-filter: blur(32px) saturate(180%); -webkit-backdrop-filter: blur(32px) saturate(180%);
  border-bottom: 1px solid rgba(34,211,238,0.07);
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 32px; position: sticky; top: 0; z-index: 30;
}
.en-header::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg,transparent,rgba(34,211,238,0.09),transparent); pointer-events: none; }

.en-huser {
  display: flex; align-items: center; gap: 10px;
  background: rgba(14,165,233,0.04); border: 1px solid rgba(34,211,238,0.1);
  border-radius: 14px; padding: 7px 16px 7px 7px;
  cursor: pointer; color: inherit;
  transition: background 0.22s, border-color 0.22s, box-shadow 0.22s, transform 0.22s;
}
.en-huser:hover { background: rgba(14,165,233,0.1); border-color: var(--en-b2); box-shadow: 0 4px 22px rgba(14,165,233,0.2); transform: translateY(-2px); }
.en-hav { width: 34px; height: 34px; border-radius: 50%; background: linear-gradient(135deg,#0ea5e9,#22d3ee); display: flex; align-items: center; justify-content: center; box-shadow: 0 0 0 2px rgba(34,211,238,0.3), 0 3px 14px rgba(14,165,233,0.28); transition: box-shadow 0.25s; }
.en-huser:hover .en-hav { box-shadow: 0 0 0 2.5px rgba(34,211,238,0.65), 0 4px 20px rgba(34,211,238,0.42); }
.en-hav svg { width: 15px; height: 15px; color: #fff; }
.en-hname { font-family: var(--en-fs); font-size: 0.88rem; font-weight: 700; color: var(--en-t1); letter-spacing: -0.01em; }
.en-hrole { font-family: var(--en-fm); font-size: 0.62rem; color: var(--en-t3); }

.en-hbtn { width: 40px; height: 40px; border-radius: 11px; background: rgba(14,165,233,0.04); border: 1px solid rgba(34,211,238,0.1); display: flex; align-items: center; justify-content: center; color: var(--en-t3); cursor: pointer; position: relative; transition: all 0.22s; }
.en-hbtn:hover { background: rgba(14,165,233,0.12); border-color: var(--en-b2); color: #67e8f9; box-shadow: 0 4px 18px rgba(14,165,233,0.2); transform: translateY(-2px) scale(1.07); }
.en-hdot { position: absolute; top: 8px; right: 8px; width: 7px; height: 7px; border-radius: 50%; background: var(--en-cyan4); border: 1.5px solid var(--en-n900); box-shadow: 0 0 10px var(--en-cyan4); animation: en-hdot 2.6s ease-in-out infinite; }
@keyframes en-hdot { 0%,80%,100%{transform:scale(1);opacity:1} 40%{transform:scale(1.7);opacity:0.4} }

/* Dropdown */
.en-dd { background: var(--en-n850)!important; border: 1px solid var(--en-b2)!important; border-radius: 18px!important; box-shadow: 0 20px 60px rgba(0,0,0,0.82)!important; overflow: hidden; min-width: 220px; font-family: var(--en-fd)!important; }
.en-ddhd { padding: 16px 20px 13px; border-bottom: 1px solid rgba(34,211,238,0.07); }
.en-ddi { padding: 11px 20px!important; font-size: 0.875rem!important; font-family: var(--en-fd)!important; color: var(--en-t2)!important; cursor: pointer!important; border-radius: 0!important; transition: background 0.15s,color 0.15s!important; display: flex!important; align-items: center!important; }
.en-ddi:hover { background: rgba(14,165,233,0.08)!important; color: var(--en-t1)!important; }
.en-ddi.danger { color: var(--en-rose)!important; }
.en-ddi.danger:hover { background: rgba(251,113,133,0.09)!important; }

/* ══ MOBILE BAR ══ */
.en-mobbar { position: fixed; top: 0; left: 0; right: 0; height: 62px; background: rgba(2,7,14,0.96); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); border-bottom: 1px solid rgba(34,211,238,0.08); display: flex; align-items: center; justify-content: space-between; padding: 0 16px; z-index: 55; }
.en-mobbtn { width: 40px; height: 40px; border-radius: 11px; background: rgba(14,165,233,0.06); border: 1px solid rgba(34,211,238,0.12); display: flex; align-items: center; justify-content: center; color: var(--en-t3); cursor: pointer; transition: all 0.2s; }
.en-mobbtn:hover { background: rgba(14,165,233,0.14); border-color: var(--en-b2); color: var(--en-cyan4); }
.en-mobov { position: fixed; inset: 0; background: rgba(1,4,10,0.9); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); z-index: 48; }

/* ══ RESPONSIVE ══ */
@media (min-width: 1024px) {
  .en-mobbar       { display: none !important; }
  .en-mob-sb       { display: none !important; }
  .en-mobile-main  { display: none !important; }
  .en-desktop-main { display: flex !important; }
}
@media (max-width: 1023px) {
  .en-sb:not(.en-mob-sb) { display: none !important; }
  .en-desktop-main        { display: none !important; }
  .en-mobbar              { display: flex !important; }
  .en-mob-sb              { display: flex !important; }
  .en-mobile-main         { display: block !important; }
}

/* ══ PAGE TRANSITION ══ */
.en-page { animation: en-pgIn 0.42s cubic-bezier(0.22,1,0.36,1) both; }
@keyframes en-pgIn { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }

/* ══ PREVENT HORIZONTAL SCROLL ══ */
html, body { overflow-x: hidden; max-width: 100vw; }
.en-main-wrap { overflow-x: hidden; min-width: 0; }
.en-page-inner { overflow-x: hidden; min-width: 0; }
.en-page-inner > * { box-sizing: border-box; max-width: 100%; }

.en-ecard { position: relative; top: 3px; }
`;

const NAV_ITEMS_EN = (t) => [
  { n: t("dashboard"),    I: LayoutDashboard, p: "EngineDashboard" },
  { n: t("transactions"), I: Receipt,          p: "EngineTransactions" },
  { n: t("deposits"),     I: ArrowDownToLine,  p: "EngineDeposits" },
  { n: t("withdrawals"),  I: ArrowUpFromLine,  p: "EngineWithdrawals" },
  { n: t("merchants"),    I: Building2,         p: "EngineMerchants" },
  { n: t("resellers"),    I: Users,             p: "EngineResellers" },
  { n: t("providers"),    I: Server,            p: "EngineProviders", badge: "VIEW" },
  { n: t("routing"),      I: Route,             p: "EngineRouting" },
  { n: t("webhooks"),     I: Webhook,           p: "EngineWebhooks" },
  { n: t("settlements"),  I: FileText,          p: "EngineSettlements" },
  { n: t("notifications"),I: Bell,              p: "Notifications" },
  { n: t("settings"),     I: Settings,          p: "EngineSettings" },
];

let enCssInjected = false;
function useEngineCSS() {
  useEffect(() => {
    if (enCssInjected) return;
    enCssInjected = true;
    const el = document.createElement("style");
    el.textContent = GLOBAL_CSS_EN;
    document.head.appendChild(el);
  }, []);
}

/* ══════════════════════════════════════════
   SIDEBAR INNER — memo, never re-mounts
══════════════════════════════════════════ */
const EngineSidebarInner = memo(function EngineSidebarInner({
  col, isMobile, nav, currentPage, resellerName, engineId, t,
  onLogoClick, onNavClick, onToggle,
}) {
  return (
    <>
      {/* Logo */}
      <Link to={createPageUrl("EngineDashboard")} onClick={onLogoClick} className="en-logo">
        <div className="en-orb"><Zap /></div>
        <AnimatePresence initial={false}>
          {(!col || isMobile) && (
            <motion.div key="en-lt"
              initial={{ opacity:0, x:-10 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:-10 }}
              transition={{ duration:0.2 }} style={{ overflow:"hidden" }}>
              <div className="en-logo-name">1401</div>
              <div className="en-logo-tag">{t("enginePanel") || "Engine Panel"}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </Link>

      {/* Nav */}
      <nav className="en-nav">
        <AnimatePresence initial={false}>
          {(!col || isMobile) && (
            <motion.div key="en-lbl-main" className="en-sec-lbl"
              initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} transition={{ duration:0.15 }}>
              Navigation
            </motion.div>
          )}
        </AnimatePresence>

        {nav.map((item) => {
          const active = currentPage === item.p;
          const isSettings = item.p === "EngineSettings";
          return (
            <React.Fragment key={item.p}>
              {isSettings && (
                <>
                  <div className="en-sep" />
                  <AnimatePresence initial={false}>
                    {(!col || isMobile) && (
                      <motion.div key="en-lbl-cfg" className="en-sec-lbl"
                        initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} transition={{ duration:0.15 }}>
                        Config
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}

              <div className={(col && !isMobile) ? "en-tipwrap" : undefined}>
                <Link
                  to={createPageUrl(item.p)}
                  onClick={(e) => onNavClick(item.p, e)}
                  className={`en-item${active ? " on" : ""}`}
                  style={(col && !isMobile) ? { justifyContent:"center", padding:"0", height:46, borderRadius:12 } : {}}
                >
                  <div className="en-ico"><item.I /></div>
                  <AnimatePresence initial={false}>
                    {(!col || isMobile) && (
                      <motion.div key={`en-l-${item.p}`}
                        initial={{ opacity:0, width:0 }} animate={{ opacity:1, width:"auto" }} exit={{ opacity:0, width:0 }}
                        transition={{ duration:0.18 }}
                        style={{ display:"flex", alignItems:"center", gap:8, overflow:"hidden", flex:1 }}>
                        <span className="en-item-txt">{item.n}</span>
                        {item.badge && <span className="en-badge">{item.badge}</span>}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Link>
                {(col && !isMobile) && <div className="en-tip">{item.n}{item.badge ? ` · ${item.badge}` : ""}</div>}
              </div>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Engine Card */}
      <AnimatePresence initial={false}>
        {(!col || isMobile) && (
          <motion.div key="en-ecard"
            initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:"auto" }} exit={{ opacity:0, height:0 }}
            transition={{ duration:0.26 }} style={{ overflow:"hidden" }}>
            <div className="en-ecard">
              <div className="en-ecard-in">
                <div className="en-ecard-top">
                  <div className="en-ecard-av"><Zap /></div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div className="en-ecard-lbl">Logged in as</div>
                    <div className="en-ecard-name">{resellerName}</div>
                  </div>
                </div>
                <div className="en-ecard-div" />
                <div className="en-ecard-bot">
                  {engineId
                    ? <><span className="en-ecard-id-lbl">ID</span><span className="en-ecard-id-val">{engineId}</span></>
                    : <><span className="en-ecard-id-lbl">Role</span><span className="en-ecard-id-val">Engine</span></>}
                  <div className="en-ecard-status"><div className="en-ecard-dot" /><span>Active</span></div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapse button — desktop only */}
      {!isMobile && (
        <div className="en-togwrap">
          <motion.button
            className={`en-tog${col ? " pill" : ""}`}
            onClick={onToggle}
            whileTap={{ scale:0.92 }}
            title={col ? "Expand sidebar" : "Collapse sidebar"}
          >
            <motion.div animate={{ rotate: col ? 180 : 0 }} transition={{ duration:0.38, ease:[0.34,1.56,0.64,1] }}>
              <ChevronLeft size={14} />
            </motion.div>
            {!col && <span className="en-tog-txt">Collapse</span>}
          </motion.button>
        </div>
      )}
    </>
  );
});

/* ══════════════════════════════════════════
   MAIN LAYOUT
══════════════════════════════════════════ */
export default function EngineLayout({
  children, currentPage,
  resellerName = "Engine Partner", engineId = "ENG001",
}) {
  useEngineCSS();

  const [col,  setCol]  = useState(false);
  const [mob,  setMob]  = useState(false);
  const navigate        = useNavigate();
  const { t }           = useLanguage();
  const nav             = NAV_ITEMS_EN(t);

  const logout = () => { auth.logout(); window.location.href = "/EngineLogin"; };

  const handleNavClick = useCallback((targetPage, e) => {
    if (!mob) return;
    e.preventDefault();
    setMob(false);
    setTimeout(() => navigate(createPageUrl(targetPage)), 320);
  }, [mob, navigate]);

  const handleLogoClick = useCallback((e) => {
    if (!mob) return;
    e.preventDefault();
    setMob(false);
    setTimeout(() => navigate(createPageUrl("EngineDashboard")), 320);
  }, [mob, navigate]);

  const sidebarW = col ? 68 : 258;

  return (
    <div style={{ minHeight:"100vh", background:"var(--en-n950)", overflowX:"hidden" }}>

      {/* MOBILE TOP BAR */}
      <div className="en-mobbar">
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button className="en-mobbtn" onClick={()=>setMob(v=>!v)} aria-label="Toggle menu">
            <AnimatePresence mode="wait" initial={false}>
              {mob
                ? <motion.span key="x" initial={{ rotate:-90,opacity:0 }} animate={{ rotate:0,opacity:1 }} exit={{ rotate:90,opacity:0 }} transition={{ duration:0.14 }}><X size={17}/></motion.span>
                : <motion.span key="m" initial={{ rotate:90,opacity:0 }} animate={{ rotate:0,opacity:1 }} exit={{ rotate:-90,opacity:0 }} transition={{ duration:0.14 }}><Menu size={17}/></motion.span>}
            </AnimatePresence>
          </button>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div className="en-orb" style={{ width:34, height:34, borderRadius:10 }}><Zap /></div>
            <span style={{ fontFamily:"var(--en-fs)", fontWeight:800, fontSize:"1.05rem", color:"var(--en-t1)" }}>1401</span>
          </div>
        </div>
        <div className="en-hbtn" style={{ position:"relative" }}>
          <Bell size={17}/><div className="en-hdot"/>
        </div>
      </div>

      {/* MOBILE OVERLAY */}
      <AnimatePresence>
        {mob && (
          <motion.div className="en-mobov"
            initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            transition={{ duration:0.2 }} onClick={()=>setMob(false)} />
        )}
      </AnimatePresence>

      {/* DESKTOP SIDEBAR */}
      <aside
        className="en-sb"
        style={{ width: sidebarW, transition:"width 0.38s cubic-bezier(0.22,1,0.36,1)" }}
      >
        <EngineSidebarInner
          col={col} isMobile={false}
          nav={nav} currentPage={currentPage}
          resellerName={resellerName} engineId={engineId}
          t={t}
          onLogoClick={handleLogoClick}
          onNavClick={handleNavClick}
          onToggle={() => setCol(v=>!v)}
        />
      </aside>

      {/* DESKTOP MAIN */}
      <div
        className="en-main-wrap en-desktop-main"
        style={{ marginLeft:sidebarW, transition:"margin-left 0.38s cubic-bezier(0.22,1,0.36,1)", minHeight:"100vh", display:"flex", flexDirection:"column", overflowX:"hidden" }}
      >
        <header className="en-header">
          <div style={{ flex:1 }} />
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <LanguageSwitcher variant="ghost" />
            <NotificationDropdown accentColor="cyan" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="en-huser">
                  <div className="en-hav"><User /></div>
                  <div style={{ textAlign:"left" }}>
                    <div className="en-hname">{resellerName}</div>
                    <div className="en-hrole">{t("engine") || "Engine"}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="en-dd">
                <div className="en-ddhd">
                  <div style={{ fontFamily:"var(--en-fs)", fontWeight:700, color:"var(--en-t1)", fontSize:"0.92rem" }}>{resellerName}</div>
                  <div style={{ fontFamily:"var(--en-fm)", fontSize:"0.65rem", color:"var(--en-t4)", marginTop:4 }}>{engineId || "Engine"}</div>
                </div>
                <DropdownMenuItem className="en-ddi" onClick={()=>navigate(createPageUrl("EngineSettings"))}>
                  <Settings style={{ width:14, height:14, marginRight:10, opacity:0.5 }} />{t("settings")}
                </DropdownMenuItem>
                <DropdownMenuSeparator style={{ background:"rgba(34,211,238,0.07)" }} />
                <DropdownMenuItem className="en-ddi danger" onClick={logout}>
                  <LogOut style={{ width:14, height:14, marginRight:10 }} />{t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main key={currentPage} className="en-page en-page-inner" style={{ flex:1, padding:"28px 32px 60px", overflowX:"hidden" }}>
          {children}
        </main>
      </div>

      {/* MOBILE SIDEBAR */}
      <aside
        className="en-sb en-mob-sb"
        style={{
          width: 270,
          transform: mob ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.32s cubic-bezier(0.22,1,0.36,1)",
          zIndex: 52,
        }}
      >
        <EngineSidebarInner
          col={false} isMobile={true}
          nav={nav} currentPage={currentPage}
          resellerName={resellerName} engineId={engineId}
          t={t}
          onLogoClick={handleLogoClick}
          onNavClick={handleNavClick}
          onToggle={() => setMob(false)}
        />
      </aside>

      {/* MOBILE MAIN */}
      <div
        className="en-mobile-main en-main-wrap"
        key={currentPage}
        style={{ paddingTop:62, overflowX:"hidden" }}
      >
        <div className="en-page en-page-inner" style={{ padding:"16px 14px 60px", overflowX:"hidden" }}>
          {children}
        </div>
      </div>
      <Toaster />
    </div>
  );
}